import { Link, Sparkles } from 'lucide-react';

import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Modal, { useModalContext } from '@/components/Modal/Modal';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import { JobStatus } from '@/features/Setting/Integrations/components/JobStatus/JobStatus';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import { toast } from '@/stores/pure/ToastStore';

import type { ScrapingJob } from '../api/dto';
import { ScrapingJobDetailContent } from '../components/ScrapingJobDetailContent/ScrapingJobDetailContent';
import { useScrapingJobDetailAction } from '../hooks/useScrapingJobDetailAction';
import {
  isCancelableScrapingJob,
  isDeletableAllFeedbackScrapingJob,
  isDeletableScrapingJob,
} from '../models/ScrapingJob';
import {
  SCRAPING_JOB_STATUS,
  SCRAPING_JOB_TYPE,
  ScrapingJobStatusToDisplayStatus,
} from '../types/constants';
import { copyScrapingJobLink } from '../utils/copyScrapingJobLink';

type Props = {
  scrapingJob: ScrapingJob;
  refetchCallback: () => void;
};

const ScrapingJobDetailModal = (props: Props) => {
  const { scrapingJob } = props;
  const { closeModal } = useModalContext();
  const workspaceId = useWorkspaceId();
  const {
    loading,
    onDeleteScheduleClick,
    onCancelJobClick,
    onDeleteAllFeedbackClick,
    onConfirmClick,
    onSummarizeWithHeySynclyClick,
    heySynclyEnabled,
  } = useScrapingJobDetailAction(scrapingJob, {
    refetchCallback: props.refetchCallback,
    onConfirmCallback: () => {
      if (scrapingJob.type === SCRAPING_JOB_TYPE.ONETIME) {
        toast('Job created successfully.', 'success');
      } else {
        toast('Scheduled created successfully.', 'success');
      }
      closeModal();
    },
    onCloseCallback: closeModal,
  });

  return (
    <Modal.Layout modalSize="m">
      <Modal.Header className="flex-row justify-between">
        <Modal.Title>{scrapingJob.title}</Modal.Title>
        <div className="flex items-center gap-3">
          {heySynclyEnabled && (
            <IconButton
              tooltip="View insight with Hey syncly"
              sizeType="m"
              icon={<Sparkles className="size-4 text-gray-700" />}
              onClick={onSummarizeWithHeySynclyClick}
              outlined={false}
            />
          )}

          <IconButton
            icon={<Link className="size-4 text-gray-700" />}
            tooltip="Copy link"
            onClick={() => {
              copyScrapingJobLink(workspaceId, scrapingJob.alias);
            }}
            outlined={false}
          />

          <div className="flex items-center gap-1">
            <JobStatus
              status={ScrapingJobStatusToDisplayStatus.get(scrapingJob.status)}
            />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <ToggleStage
          status={true}
          className="min-h-[330px]"
          fallback={
            <div className="flex items-center justify-center w-full h-[330px]">
              <Spinner />
            </div>
          }
        >
          <ScrapingJobDetailContent scrapingJob={scrapingJob} />
        </ToggleStage>
      </Modal.Body>
      <Modal.Footer
        additional={(function () {
          if (isDeletableScrapingJob(scrapingJob)) {
            return {
              sizeType: 'm',
              variant: 'error_outlined',
              children: 'Delete schedule',
              onClick: onDeleteScheduleClick,
            };
          }
          if (isCancelableScrapingJob(scrapingJob)) {
            return {
              sizeType: 'm',
              variant: 'error_outlined',
              children: 'Cancel job',
              onClick: onCancelJobClick,
            };
          }
          if (isDeletableAllFeedbackScrapingJob(scrapingJob)) {
            return {
              sizeType: 'm',
              variant: 'error_outlined',
              children: 'Delete all feedback',
              onClick: onDeleteAllFeedbackClick,
            };
          }
        })()}
        cancel={{
          sizeType: 'm',
          variant: 'secondary',
          children: 'Close',
          onClick: closeModal,
        }}
        confirm={
          scrapingJob?.status === SCRAPING_JOB_STATUS.BEFORE_CONFIRM
            ? {
                sizeType: 'm',
                variant: 'primary',
                children: 'Approve',
                onClick: onConfirmClick,
                isLoading: loading,
              }
            : undefined
        }
      />
    </Modal.Layout>
  );
};

export default ScrapingJobDetailModal;
